import React, { useEffect, useState } from "react";
import api, { API } from "@/lib/api";
import { useApp } from "@/contexts/AppContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { formatSAR, formatDate, statusColor } from "@/lib/utils";
import { Download } from "lucide-react";
import { ResponsiveContainer, ComposedChart, Bar, Line, XAxis, YAxis, Tooltip, Legend, CartesianGrid } from "recharts";

function TrendChart() {
  const { t } = useApp();
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/reports/trend?months=6").then((r) => setItems(r.data.items)); }, []);
  return (
    <Card data-testid="trend-chart"><CardHeader><CardTitle className="text-base">{t("trend_title")}</CardTitle></CardHeader>
      <CardContent className="h-72">
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={items} margin={{ left: 0, right: 10 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
            <XAxis dataKey="month" tick={{ fontSize: 12 }} />
            <YAxis yAxisId="l" tick={{ fontSize: 12 }} />
            <YAxis yAxisId="r" orientation="right" tick={{ fontSize: 12 }} />
            <Tooltip formatter={(v, n) => (n === t("trend_bookings") ? v : formatSAR(v))} />
            <Legend />
            <Bar yAxisId="r" dataKey="gross" name={t("trend_gross")} fill="#a7f3d0" radius={[6, 6, 0, 0]} />
            <Bar yAxisId="r" dataKey="paid" name={t("trend_paid")} fill="#059669" radius={[6, 6, 0, 0]} />
            <Line yAxisId="l" type="monotone" dataKey="bookings" name={t("trend_bookings")} stroke="#0f172a" strokeWidth={2} />
          </ComposedChart>
        </ResponsiveContainer>
      </CardContent></Card>
  );
}

function Stat({ label, value, testid }) {
  return <div className="abdan-card p-5"><div className="text-xs uppercase tracking-widest text-slate-500 font-semibold">{label}</div><div className="text-2xl font-bold mt-1" data-testid={testid}>{value}</div></div>;
}

function Breakdown({ title, data, labelFn }) {
  const entries = Object.entries(data || {});
  return (
    <Card><CardHeader><CardTitle className="text-base">{title}</CardTitle></CardHeader>
      <CardContent className="space-y-2">
        {entries.length === 0 && <div className="text-sm text-slate-500">-</div>}
        {entries.map(([k, v]) => <div key={k} className="flex justify-between text-sm"><span>{labelFn(k)}</span><b>{v}</b></div>)}
      </CardContent></Card>
  );
}

export function AdminReports() {
  const { t } = useApp();
  const now = new Date();
  const [ym, setYm] = useState(`${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`);
  const [d, setD] = useState(null);
  const [year, month] = ym.split("-").map(Number);
  useEffect(() => { api.get(`/reports/monthly?year=${year}&month=${month}`).then((r) => setD(r.data)); }, [year, month]);
  const exportCsv = async () => {
    const r = await api.get(`/reports/monthly.csv?year=${year}&month=${month}`, { responseType: "blob" });
    const url = URL.createObjectURL(r.data); const a = document.createElement("a");
    a.href = url; a.download = `rekap-${ym}.csv`; a.click(); URL.revokeObjectURL(url);
  };
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <h1 className="text-3xl font-bold">{t("monthly_report")}</h1>
        <Input type="month" value={ym} onChange={(e) => setYm(e.target.value)} className="w-44 ml-auto" data-testid="rpt-month" />
        <Button onClick={exportCsv} className="bg-emerald-600 hover:bg-emerald-700" data-testid="rpt-export"><Download className="w-4 h-4 mr-2" />Export CSV</Button>
      </div>
      <TrendChart />
      {d && <>
        <div className="grid md:grid-cols-4 gap-4">
          <Stat label={t("booking")} value={d.total_bookings} testid="rpt-total" />
          <Stat label={t("gross_value")} value={formatSAR(d.gross_value)} testid="rpt-gross" />
          <Stat label={t("revenue_paid")} value={formatSAR(d.revenue_paid)} testid="rpt-paid" />
          <Stat label={t("dp_collected")} value={formatSAR(d.dp_collected)} testid="rpt-dp" />
        </div>
        <div className="grid md:grid-cols-3 gap-4">
          <Breakdown title={t("by_service")} data={d.by_service} labelFn={(k) => t(`svc_${k}`)} />
          <Breakdown title={t("by_vehicle")} data={d.by_vehicle} labelFn={(k) => k} />
          <Breakdown title={t("by_status")} data={d.by_status} labelFn={(k) => t(`st_${k}`)} />
        </div>
        <Card><CardContent className="p-2 overflow-x-auto">
          <Table>
            <TableHeader><TableRow><TableHead>Invoice</TableHead><TableHead>{t("pickup_date")}</TableHead><TableHead>{t("customer")}</TableHead><TableHead>{t("route")}</TableHead><TableHead>{t("vehicle_type")}</TableHead><TableHead>{t("price")}</TableHead><TableHead>{t("status")}</TableHead><TableHead>{t("payment")}</TableHead></TableRow></TableHeader>
            <TableBody>
              {d.rows.length === 0 && <TableRow><TableCell colSpan={8} className="text-center py-8 text-slate-500">{t("no_data")}</TableCell></TableRow>}
              {d.rows.map((b) => <TableRow key={b.id}><TableCell className="font-mono text-xs">{b.invoice_no}<br />{b.code}</TableCell><TableCell className="text-sm">{formatDate(b.pickup_date)}</TableCell><TableCell>{b.customer_name}</TableCell><TableCell className="text-sm">{b.route || `${b.origin} → ${b.destination}`}</TableCell><TableCell>{b.vehicle_type}</TableCell><TableCell className="font-semibold">{formatSAR(b.price)}</TableCell><TableCell><Badge className={statusColor(b.status)}>{t(`st_${b.status}`)}</Badge></TableCell><TableCell><Badge className={statusColor(b.payment_status)}>{t(`pay_${b.payment_status}`)}</Badge></TableCell></TableRow>)}
            </TableBody>
          </Table>
        </CardContent></Card>
      </>}
    </div>
  );
}
